<script setup lang="ts">
import { ref, nextTick, watch, onMounted, onUnmounted } from 'vue'

import Speedometer from './Speedometer.vue'
import ComplianceChart from './ComplianceChart.vue'
import HorizontalBar from './HorizontalBar.vue'

import type { PropType } from 'vue'
import type { IChartDataItem } from '@/components/common/common'

const props = defineProps({
  speedometerData: {
    type: Array as PropType<IChartDataItem[]>,
    required: true,
    validator: (value: IChartDataItem[]) => {
      if (value.length > 2) {
        console.error('The "data" prop can contain at most 2 items.')
        return false
      }
      return true
    }
  },
  complianceData: {
    type: Array as PropType<IChartDataItem[]>,
    required: false,
    validator: (value: IChartDataItem[]) => {
      if (value.length > 2) {
        console.error('The "data" prop can contain at most 2 items.')
        return false
      }
      return true
    }
  },
  colorProps: {
    type: Object,
    required: false
  },
  expanded: {
    type: Boolean,
    default: true
  },
  position: {
    type: String,
    default: 'top'
  }
})

const showChildren = ref(props.expanded)
const containerHeight = ref<string | number>(props.expanded ? 'fit-content' : 0)
const container = ref<HTMLElement | null>(null)
const strokeWidth = ref<string>('0.5rem')
let resizeObserver: ResizeObserver | null = null

const updateStrokeWidth = () => {
  if (!container.value) return
  const heightPx = container.value.clientHeight || 0
  // Scale stroke width with container height, clamped to a sensible range
  const strokePx = Math.max(7, Math.min(12, heightPx * 0.08))
  strokeWidth.value = `${strokePx / 36}rem`
}

watch(() => props.expanded, (newVal) => {
  if (newVal) {
    showChildren.value = true
    nextTick(() => {
      if (container.value) {
        container.value.style.opacity = '0'
        containerHeight.value = `${container.value.scrollHeight}px`
        setTimeout(() => {
          containerHeight.value = 'fit-content'
          if (container.value) {
            container.value.style.overflow = 'visible'
            container.value.style.opacity = '1'
            updateStrokeWidth()
          }
        }, 400)
      }
    })
  } else if (container.value) {
    showChildren.value = false
    containerHeight.value = `${container.value.scrollHeight}px`
    container.value.style.overflow = 'hidden'
    container.value!.style.opacity = '0'
    setTimeout(() => {
      containerHeight.value = '0px'
    }, 10)
  }
})

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => updateStrokeWidth())
    if (container.value) {
      resizeObserver.observe(container.value)
    }
  }
  updateStrokeWidth()
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<template>
  <div
    class="portfolio-item-outer"
    :class="{
      top: position === 'top',
      bottom: position === 'bottom'
    }"
  >
    <slot name="headerLogo" />
    <slot name="title" />
    <div
      class="dynmaic-container"
      v-show="showChildren"
      ref="container"
      :style="{ height: containerHeight }"
    >
      <Speedometer
        v-if="expanded"
        :data="speedometerData"
        :colors="colorProps"
        :strokeWidth="strokeWidth"
      />
      <ComplianceChart v-if="complianceData && expanded" :data="complianceData">
        <template #logo><slot name="logo" /></template>
      </ComplianceChart>
    </div>
    <HorizontalBar v-show="!expanded" :data="speedometerData" :colors="colorProps" />
  </div>
</template>

<style scoped>
.portfolio-item-outer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 15.625rem;
  position: relative;
}

/* .portfolio-item-outer.top {
  padding-top: 10%;
} */

/* .portfolio-item-outer.bottom {
  padding-bottom: 10%;
} */

.portfolio-item-outer .dynmaic-container {
  width: 100%;
  overflow: hidden;
  transition:
    height 0.5s ease,
    opacity 0.2s ease-in;
}
</style>
